/**
 * SVG `<linearGradient>` / `<radialGradient>` → editor `ShapeFillValue`.
 *
 * Tier 2 Group F SVG round-trip polish — closes the deferred
 * "native gradient mapping" item from the SVG import plan.
 *
 * ## What this does
 *
 * - Walks `<defs>` (and any other location SVG allows gradient
 *   definitions) to build a map of gradient-id → parsed structure.
 * - Resolves `<linearGradient xlink:href="#other">` inheritance
 *   chains — SVG allows a gradient to reference another to inherit
 *   its stops; we follow up to 10 hops to terminate any cycle.
 * - Parses each gradient's stops, baking `stop-opacity` into an
 *   8-digit hex color (`#rrggbbaa`) so the editor's stop-color-only
 *   model preserves transparency through round-trip.
 * - Computes an effective angle (degrees, clockwise from +x) for
 *   linear gradients from `x1/y1/x2/y2`, optionally rotated by the
 *   gradient's own `gradientTransform`. The editor's `linear`
 *   gradient model stores only `angle` + stops (no absolute coords);
 *   the gradient renders perpendicular to the angle across the
 *   element's bbox.
 * - Maps radial gradients to the editor's `radial` shape — which has
 *   only stops (no cx/cy/r/fx/fy). Necessarily lossy: the gradient
 *   re-renders centered at the element's bbox center, full-radius
 *   from there. Suitable for typical "highlight-from-center" designs;
 *   off-center / focal-point gradients lose their offset.
 *
 * ## What this does NOT do
 *
 * - **Pattern fills (`<pattern>`)** stay rasterized. The editor's
 *   `ShapeFillValue` model has no pattern variant; mapping would
 *   require a model expansion that's out of scope here.
 * - **Mesh gradients (`<meshgradient>` from SVG 2)** stay rasterized.
 *   No browser ships a stable implementation today and the editor
 *   model doesn't support mesh either.
 * - **Per-stop opacity that can't be baked into the color** — fully
 *   transparent stops survive (alpha=00), and partially transparent
 *   stops on top of a colored base also survive. The lossy case is
 *   if a single gradient mixes opacity values that would imply
 *   blending: we bake the literal alpha into each stop's color and
 *   let the renderer handle blending the resulting `#rrggbbaa`
 *   stops. This matches Figma / Sketch's import behavior.
 *
 * Module is intentionally side-effect-free and DOM-light — it
 * accepts `Element` / `XMLDocument` arguments from the caller and
 * uses only the standard DOM tree traversal API. Browser, happy-dom,
 * and jsdom all ship the surface we need.
 */

import type { ShapeFillValue, ShapeGradientStop } from '../types';

/** Maximum hops we follow when resolving a gradient's
 *  `xlink:href` / `href` chain. A pathological SVG could declare
 *  cyclic references; we cap at 10 hops to terminate cleanly.
 *  Browsers tolerate cycles but render the chain as if it were
 *  cut at the first repeat — our behavior matches. */
const MAX_GRADIENT_HREF_HOPS = 10;

/** Parsed metadata for a single gradient definition, before
 *  resolution into the editor's `ShapeFillValue`. We keep the
 *  intermediate shape so the inheritance walk can compose
 *  attributes from a parent gradient. */
interface ParsedGradient {
  kind: 'linear' | 'radial';
  /** Stops from this element OR inherited from the href target.
   *  When an element declares its own stops they override the
   *  parent's; when it doesn't, the parent's pass through. */
  stops: ShapeGradientStop[] | null;
  /** Linear gradient endpoints (any/all may be absent — defaults
   *  fill in below). When `gradientUnits = 'userSpaceOnUse'`, these
   *  are absolute coordinates; for `objectBoundingBox` (default),
   *  they're in [0,1] relative to the element's bbox. Both forms
   *  resolve to the same angle calculation since we only care
   *  about the vector direction. */
  x1?: string;
  y1?: string;
  x2?: string;
  y2?: string;
  /** Gradient's own transform matrix, if any. Composed with the
   *  endpoint coords to compute the final angle. */
  gradientTransform: string | null;
  /** Whether this gradient inherits from another (chain root). */
  hrefTarget: string | null;
}

/** Public surface — a gradient resolved to the editor's runtime
 *  shape, ready to drop into a `VectorPath['fill']` slot. */
export type ResolvedGradient = Extract<ShapeFillValue, { type: 'linear' | 'radial' }>;

/** Build the gradient-id → parsed structure map by walking the
 *  whole document. Iterates over every `<linearGradient>` /
 *  `<radialGradient>` regardless of ancestor — SVG allows them
 *  anywhere, though `<defs>` is conventional. */
export function extractGradientDefs(doc: XMLDocument): Map<string, ParsedGradient> {
  const map = new Map<string, ParsedGradient>();
  const linears = doc.getElementsByTagName('linearGradient');
  for (let i = 0; i < linears.length; i++) {
    const el = linears[i];
    const id = el.getAttribute('id');
    if (!id) continue;
    map.set(id, parseGradientElement(el, 'linear'));
  }
  const radials = doc.getElementsByTagName('radialGradient');
  for (let i = 0; i < radials.length; i++) {
    const el = radials[i];
    const id = el.getAttribute('id');
    if (!id) continue;
    map.set(id, parseGradientElement(el, 'radial'));
  }
  return map;
}

/** Parse one gradient element into the intermediate shape. Stops
 *  come from direct `<stop>` children; an empty stop list signals
 *  the gradient inherits from `href`. */
function parseGradientElement(el: Element, kind: 'linear' | 'radial'): ParsedGradient {
  const stops = parseGradientStops(el);
  const hrefTarget = readHrefTarget(el);
  return {
    kind,
    stops: stops.length > 0 ? stops : null,
    x1: el.getAttribute('x1') ?? undefined,
    y1: el.getAttribute('y1') ?? undefined,
    x2: el.getAttribute('x2') ?? undefined,
    y2: el.getAttribute('y2') ?? undefined,
    gradientTransform: el.getAttribute('gradientTransform'),
    hrefTarget,
  };
}

/** Resolve `xlink:href="#id"` or `href="#id"` — return the target
 *  id without the `#` prefix, or null if neither attribute is set
 *  (or set to a non-fragment href like `https://...`). */
function readHrefTarget(el: Element): string | null {
  // SVG 2 allows the plain `href`; SVG 1.1 used `xlink:href`. We
  // accept both, with the unprefixed form winning on conflict
  // (matches the spec's resolution order).
  const href = el.getAttribute('href') ?? el.getAttribute('xlink:href');
  if (!href) return null;
  const trimmed = href.trim();
  if (!trimmed.startsWith('#')) return null;
  return trimmed.slice(1);
}

/** Parse `<stop>` children into editor stops. Each stop gains an
 *  8-digit hex color (`#rrggbbaa`) so any `stop-opacity` is
 *  preserved through the editor's color-string-only model. */
function parseGradientStops(el: Element): ShapeGradientStop[] {
  const stops: ShapeGradientStop[] = [];
  const children = el.getElementsByTagName('stop');
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    const offsetRaw = child.getAttribute('offset');
    const offset = parseStopOffset(offsetRaw);
    if (offset === null) continue;
    // stop-color may be on the attribute or in `style`; the latter
    // wins on conflict per CSS cascading order. Same for stop-opacity.
    const styleAttr = child.getAttribute('style') ?? '';
    const colorRaw =
      readStyleProp(styleAttr, 'stop-color') ?? child.getAttribute('stop-color') ?? '#000000';
    const opacityRaw =
      readStyleProp(styleAttr, 'stop-opacity') ?? child.getAttribute('stop-opacity');
    const opacity = parseOpacity(opacityRaw);
    const color = bakeOpacityIntoColor(colorRaw, opacity);
    stops.push({ offset, color });
  }
  return stops;
}

/** Parse a `stop-offset` value. Accepts `0`, `0.5`, `1`, `50%`,
 *  or anything `parseFloat`-friendly. Returns:
 *    - `0` when the attribute is absent or empty (SVG default —
 *      a stop at the start of the gradient)
 *    - clamped numeric value (0..1) for valid numbers / percentages
 *    - `null` only when the input is present but unparseable
 *      (caller skips the stop) */
function parseStopOffset(raw: string | null): number | null {
  if (raw === null) return 0; // SVG default
  const trimmed = raw.trim();
  if (!trimmed) return 0;
  if (trimmed.endsWith('%')) {
    const n = parseFloat(trimmed.slice(0, -1));
    if (Number.isNaN(n)) return null;
    return clamp01(n / 100);
  }
  const n = parseFloat(trimmed);
  if (Number.isNaN(n)) return null;
  return clamp01(n);
}

function parseOpacity(raw: string | null | undefined): number {
  if (!raw) return 1;
  const trimmed = raw.trim();
  if (!trimmed) return 1;
  const n = parseFloat(trimmed);
  if (Number.isNaN(n)) return 1;
  return clamp01(n);
}

function clamp01(n: number): number {
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

/** Bake an opacity value into a color string by converting to an
 *  8-digit hex (`#rrggbbaa`). The editor's color renderer
 *  (Fabric.js paths) supports this format natively. We normalize
 *  the input color via a simple named-color / 3-digit / 6-digit
 *  hex parser; out-of-recognized-format inputs (e.g. `rgb()`,
 *  `hsl()`, custom CSS color functions) pass through with the
 *  alpha appended — Fabric handles the conversion.
 *
 *  Opacity defaults to 1; when 1, returns the color unchanged so
 *  round-trip output stays clean. */
export function bakeOpacityIntoColor(color: string, opacity: number): string {
  if (opacity >= 1) return color;
  const trimmed = color.trim();
  // 6-digit hex → append alpha
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) {
    return `${trimmed}${alphaHex(opacity)}`;
  }
  // 3-digit hex → expand to 6, then append alpha
  if (/^#[0-9a-fA-F]{3}$/.test(trimmed)) {
    const r = trimmed[1];
    const g = trimmed[2];
    const b = trimmed[3];
    return `#${r}${r}${g}${g}${b}${b}${alphaHex(opacity)}`;
  }
  // 8-digit hex with explicit alpha — multiply existing alpha by
  // this opacity (both contribute to the final transparency).
  if (/^#[0-9a-fA-F]{8}$/.test(trimmed)) {
    const existingAlpha = parseInt(trimmed.slice(7, 9), 16) / 255;
    return `${trimmed.slice(0, 7)}${alphaHex(existingAlpha * opacity)}`;
  }
  // rgb()/rgba()/named-color/etc. — convert to rgba() with the
  // baked opacity. Best we can do without a full CSS color parser;
  // Fabric will read this just fine.
  if (/^rgba?\(/i.test(trimmed)) {
    // Replace the alpha component if present, else append.
    const inner = trimmed.replace(/^rgba?\(\s*|\s*\)$/gi, '');
    const parts = inner.split(',').map((s) => s.trim());
    const r = parts[0] ?? '0';
    const g = parts[1] ?? '0';
    const b = parts[2] ?? '0';
    return `rgba(${r}, ${g}, ${b}, ${opacity.toFixed(3)})`;
  }
  // Named colors / unknown — wrap as rgba() via the color itself
  // by trusting the renderer. We just have to communicate opacity
  // somehow; if the renderer can resolve the named color it'll
  // honor our explicit alpha here.
  return `${trimmed}/${opacity.toFixed(3)}`;
}

function alphaHex(opacity: number): string {
  const clamped = Math.round(clamp01(opacity) * 255);
  return clamped.toString(16).padStart(2, '0');
}

/** Read a CSS-shape inline-style property value out of a `style`
 *  attribute string. Returns null when not present. Conservative
 *  parse — splits on `;`, finds the matching key, returns the
 *  value. Doesn't handle CSS comments / quoted strings (rare in
 *  SVG inline style). */
function readStyleProp(styleAttr: string, prop: string): string | null {
  const lower = prop.toLowerCase();
  for (const part of styleAttr.split(';')) {
    const colonIdx = part.indexOf(':');
    if (colonIdx < 0) continue;
    const key = part.slice(0, colonIdx).trim().toLowerCase();
    if (key === lower) {
      return part.slice(colonIdx + 1).trim();
    }
  }
  return null;
}

/** Walk the href chain from `startId` until either:
 *   - the target gradient has its own stops → return those stops
 *     plus a merged set of endpoint coords (child wins on conflict)
 *   - we exceed `MAX_GRADIENT_HREF_HOPS` → return the most-recent
 *     gradient's state as-is (the SVG is malformed; do our best)
 *   - the chain dead-ends (next id has no gradient) → return the
 *     last successful gradient's state
 *
 *  Returns null when the starting id has no gradient at all. */
function resolveInheritedGradient(
  startId: string,
  gradients: Map<string, ParsedGradient>,
): ParsedGradient | null {
  const start = gradients.get(startId);
  if (!start) return null;
  let cur = start;
  // Compose: walk the chain to find stops + collect endpoint
  // attrs. The closest gradient to the start wins on per-attr
  // conflict (SVG inheritance is most-specific-wins from the
  // child).
  const composed: ParsedGradient = { ...cur };
  // Increment-at-loop-entry so the hop cap fires AT the boundary,
  // not after one extra iteration. (Pre-PR review Critical #2 —
  // earlier post-increment let an 11-hop chain process before the
  // cap kicked in.) Tracks all gradients visited so a cycle
  // (`a → b → a`) terminates as soon as we revisit `a` — robust
  // against MAX_GRADIENT_HREF_HOPS regardless of cycle length.
  const visited = new Set<string>([startId]);
  while (composed.stops === null && cur.hrefTarget && visited.size < MAX_GRADIENT_HREF_HOPS) {
    if (visited.has(cur.hrefTarget)) break;
    const next = gradients.get(cur.hrefTarget);
    if (!next) break;
    visited.add(cur.hrefTarget);
    // Stops inherit from parent if child didn't declare them.
    if (composed.stops === null && next.stops !== null) {
      composed.stops = next.stops;
    }
    // Endpoint coords inherit from parent if child didn't declare
    // them. (Child's attribute presence wins; "absent" inherits.)
    if (composed.x1 === undefined) composed.x1 = next.x1;
    if (composed.y1 === undefined) composed.y1 = next.y1;
    if (composed.x2 === undefined) composed.x2 = next.x2;
    if (composed.y2 === undefined) composed.y2 = next.y2;
    if (composed.gradientTransform === null) composed.gradientTransform = next.gradientTransform;
    cur = next;
  }
  return composed;
}

/** Parse a comma-or-space-separated 6-number transform matrix string.
 *  Accepts the most common SVG transform forms: `matrix(a b c d e f)`,
 *  `rotate(deg)`, `rotate(deg cx cy)`, `translate(tx)`, `translate(tx ty)`,
 *  `scale(s)`, `scale(sx sy)`, `skewX(deg)`, `skewY(deg)`. Returns
 *  the resulting affine as `[a, b, c, d, e, f]` (column-major:
 *  `[ a c e ; b d f ]`). Returns the identity matrix when input
 *  is empty / unparseable. */
function parseSvgTransformMatrix(
  transform: string | null,
): [number, number, number, number, number, number] {
  if (!transform) return [1, 0, 0, 1, 0, 0];
  // Identity to compose against.
  let m: [number, number, number, number, number, number] = [1, 0, 0, 1, 0, 0];
  const re = /(matrix|rotate|translate|scale|skewX|skewY)\s*\(([^)]*)\)/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(transform)) !== null) {
    const op = match[1];
    const nums = match[2]
      .split(/[\s,]+/)
      .filter(Boolean)
      .map(Number);
    let next: [number, number, number, number, number, number] | null = null;
    if (op === 'matrix' && nums.length === 6) {
      next = [nums[0], nums[1], nums[2], nums[3], nums[4], nums[5]];
    } else if (op === 'translate') {
      const tx = nums[0] ?? 0;
      const ty = nums[1] ?? 0;
      next = [1, 0, 0, 1, tx, ty];
    } else if (op === 'scale') {
      const sx = nums[0] ?? 1;
      const sy = nums[1] ?? sx;
      next = [sx, 0, 0, sy, 0, 0];
    } else if (op === 'rotate') {
      const deg = nums[0] ?? 0;
      const rad = (deg * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      if (nums.length >= 3) {
        // Rotate around (cx, cy): translate(cx,cy) rotate(deg) translate(-cx,-cy).
        const cx = nums[1];
        const cy = nums[2];
        // Pre-multiply translation(cx,cy) * rotation(deg) * translation(-cx,-cy)
        const tx = cx - cx * cos + cy * sin;
        const ty = cy - cx * sin - cy * cos;
        next = [cos, sin, -sin, cos, tx, ty];
      } else {
        next = [cos, sin, -sin, cos, 0, 0];
      }
    } else if (op === 'skewX') {
      const t = Math.tan(((nums[0] ?? 0) * Math.PI) / 180);
      next = [1, 0, t, 1, 0, 0];
    } else if (op === 'skewY') {
      const t = Math.tan(((nums[0] ?? 0) * Math.PI) / 180);
      next = [1, t, 0, 1, 0, 0];
    }
    if (next) {
      // Compose: m = m × next.
      m = [
        m[0] * next[0] + m[2] * next[1],
        m[1] * next[0] + m[3] * next[1],
        m[0] * next[2] + m[2] * next[3],
        m[1] * next[2] + m[3] * next[3],
        m[0] * next[4] + m[2] * next[5] + m[4],
        m[1] * next[4] + m[3] * next[5] + m[5],
      ];
    }
  }
  return m;
}

/** Apply a 2D affine matrix to a vector (direction only, no
 *  translation). Used to rotate the gradient's endpoint
 *  vector through `gradientTransform`. */
function applyMatrixToVector(
  m: [number, number, number, number, number, number],
  x: number,
  y: number,
): { x: number; y: number } {
  return {
    x: m[0] * x + m[2] * y,
    y: m[1] * x + m[3] * y,
  };
}

/** Parse a coordinate value that may be a `0..1` number (the
 *  default `objectBoundingBox` units), a percentage like `50%`,
 *  or a `userSpaceOnUse` pixel value. Percentages are normalized
 *  to [0,1]; non-percentage values are returned as-is.
 *
 *  Only the DIRECTION VECTOR matters for the editor's angle-only
 *  gradient model — the angle is scale-invariant (`atan2(dy, dx)`
 *  yields 45° for both `(1, 1)` and `(500, 500)`). Magnitude is
 *  discarded. If a future caller needs the absolute coords (e.g.
 *  for native bbox-fit gradient emission), this function should
 *  be revisited to surface the unit type. (Pre-PR review
 *  Critical #3.) */
function parseCoord(raw: string | undefined, fallback: number): number {
  if (raw === undefined) return fallback;
  const trimmed = raw.trim();
  if (!trimmed) return fallback;
  if (trimmed.endsWith('%')) {
    const n = parseFloat(trimmed.slice(0, -1));
    return Number.isNaN(n) ? fallback : n / 100;
  }
  const n = parseFloat(trimmed);
  return Number.isNaN(n) ? fallback : n;
}

/** Resolve a `url(#id)` fill or stroke reference to the editor's
 *  `ShapeFillValue`. Returns null when the id doesn't resolve to
 *  a parseable gradient (no matching def, pattern, mesh, etc.).
 *
 *  Caller is responsible for detecting the `url(#id)` syntax —
 *  this function takes a bare id (no `url()` wrapper, no `#`).
 *  Pull the id from the attribute via `parseUrlRef` below. */
export function resolveFillRef(
  id: string,
  gradients: Map<string, ParsedGradient>,
): ResolvedGradient | null {
  const grad = resolveInheritedGradient(id, gradients);
  if (!grad) return null;
  if (!grad.stops || grad.stops.length === 0) return null;
  if (grad.kind === 'linear') {
    // Default endpoints: x1=0%, y1=0%, x2=100%, y2=0% (left → right).
    // Treat percentages and userSpaceOnUse the same way — for the
    // editor's angle-only model, only the vector direction matters,
    // not the absolute scale.
    const x1 = parseCoord(grad.x1, 0);
    const y1 = parseCoord(grad.y1, 0);
    const x2 = parseCoord(grad.x2, 1);
    const y2 = parseCoord(grad.y2, 0);
    // Rotate the direction vector through gradientTransform.
    const m = parseSvgTransformMatrix(grad.gradientTransform);
    const start = applyMatrixToVector(m, x1, y1);
    const end = applyMatrixToVector(m, x2, y2);
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    // Degenerate zero-length vector → assume 0° (left-to-right).
    if (dx === 0 && dy === 0) {
      return { type: 'linear', angle: 0, stops: grad.stops };
    }
    const angleRad = Math.atan2(dy, dx);
    const angleDeg = (angleRad * 180) / Math.PI;
    // Normalize to [0, 360).
    const normalized = ((angleDeg % 360) + 360) % 360;
    return { type: 'linear', angle: normalized, stops: grad.stops };
  }
  // Radial — editor only has stops + implicit bbox-center.
  return { type: 'radial', stops: grad.stops };
}

/** Extract the bare id from a `url(#id)` attribute value. Returns
 *  null when the value isn't a fragment-only url-ref. SVG also
 *  supports external refs like `url(file.svg#id)` — those are
 *  treated as unresolvable here (the editor can't follow external
 *  refs at import time). */
export function parseUrlRef(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  const match = /^url\s*\(\s*['"]?#([^'")\s]+)['"]?\s*\)/i.exec(trimmed);
  if (!match) return null;
  return match[1];
}
